import React, { useState } from 'react';

import dayjs, { extend } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import { useGamesContent } from '@pkg/hooks';

import { Period } from '@app/client';
import { useNavigationStore } from '@app/store/zustand';

import { NavButton } from '../../NavButton';
import { Toggle } from '../../Toggle';
import { collapseTransition, groupAnimation } from '../../animations';
import { Buttons, Collapse, Collapseable } from '../../styles';

import { GameListItem } from './GameListItem';

import { GameItemsWrapper, GameSkeleton, Status } from './styles';

extend(isBetween);

interface SectionProps {
    isOpen: boolean;
    isStatic?: boolean;
    setIsOpen?: (isOpen: boolean) => void;
}

export const YatFantasySection = ({ isOpen, setIsOpen, isStatic }: SectionProps) => {
    const { activeGames, activeLoading: isLoading } = useGamesContent({ period: Period.Present });

    const [openId, setOpenId] = useState(activeGames?.[0]?.id || '');
    const [noTransition, setNoTransition] = useState(true);
    const openGames = Boolean(activeGames?.length);
    const toggleMobileMenu = useNavigationStore((state) => state.toggleMobileMenu);
    const now = dayjs();
    const subsOpen = activeGames.some((g) => {
        return now.isBefore(dayjs(g.submission_ends_at));
    });

    const menuStatus = openGames ? `Submissions  ${subsOpen ? 'open' : 'closed'}` : 'No active games';

    const handleOpenGame = (open: boolean, id: string) => {
        setOpenId(open ? id : '');
    };

    return (
        <Collapseable>
            <Toggle
                emoji="🦄"
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                noTransition={noTransition}
                setNoTransition={setNoTransition}
                isStatic={isStatic}
                showAlways={
                    !isLoading && <Status $submissionState={openGames ? 'open' : undefined}>{menuStatus}</Status>
                }
                aria-expanded={isOpen}
                aria-controls="sidenav-section-fantasy"
            >
                Yat Fantasy
            </Toggle>
            <Collapse
                variants={groupAnimation}
                initial={'open'}
                animate={isOpen ? 'open' : 'closed'}
                transition={noTransition ? { duration: 0 } : collapseTransition}
                aria-labelledby="sidenav-section-fantasy"
                $maxHeight={300}
            >
                <Buttons>
                    <GameItemsWrapper>
                        {isLoading &&
                            Array.from({ length: 2 }, (_, index) => (
                                <GameSkeleton key={`fantasy-sidebar-skeleton-${index}`} />
                            ))}

                        {!isLoading && activeGames?.length
                            ? activeGames?.map((game, index) => {
                                  if (!game) return <GameSkeleton key={`fantasy-sidebar-skeleton-${index}`} />;
                                  const isSubmitted = Boolean(game?.wishlists?.length);
                                  return (
                                      <GameListItem
                                          key={`${game.id}-${index}`}
                                          competition={game}
                                          isSubmitted={isSubmitted}
                                          isGameOpen={openId === game.id}
                                          canParticipate={now.isBefore(dayjs(game.submission_ends_at)) || isSubmitted}
                                          onSetGameOpen={handleOpenGame}
                                      />
                                  );
                              })
                            : null}

                        <NavButton
                            to={`/fantasy`}
                            icon="discover"
                            isActive={location.pathname == `/fantasy`}
                            onClick={() => toggleMobileMenu(false)}
                        >
                            View all games
                        </NavButton>
                    </GameItemsWrapper>
                </Buttons>
            </Collapse>
        </Collapseable>
    );
};
